import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, Observable, of, Subject} from 'rxjs';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import {AdxPaymentFilter, AdxPaymentResponse, CreateAdxPaymentModel, SearchAdxPaymentParams} from '../models/adx-payment';
import moment from 'moment';
import {AdxPaymentReconciliationAdjustReq, AdxPaymentReconciliationResponse, PaymentReconciliationFilter, SearchAdxPaymentReconciliationParams, SearchReconciliationParams} from '../models/client-reconciliation';
import { AdxPaymentReconciliationAdjustmentOrgReq } from '../models/reconciliation-model';

export interface InvoiceDetails {
  infoBlock: {
    client: {
      firstName: string;
      middleName: string;
      lastName: string;
      name: string;
      isCorporateClient: boolean;
      id: number;
      profileNo: number;
    };
    fmsInvoiceNumber: number;
    issueDate: string;
    recordLocator: string;
  };
  bookings: any[];
  paymentDetails: {
    payments: Array<{
      id: number;
      payMethod: string;
      paymentNumber: string;
      amountPaid: number;
      currencyCode: string;
    }>;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AdxPaymentService {
  private _dataSourceReceived$ = new BehaviorSubject<AdxPaymentFilter | {}>({});
  private _dataSourceMade$ = new BehaviorSubject<AdxPaymentFilter | {}>({});
  private refreshAdxPaymentTrigger = new Subject<void>();
  refreshAdxPaymentGrid$ = this.refreshAdxPaymentTrigger.asObservable();
  private _dataSourceReconciliation$ = new BehaviorSubject<PaymentReconciliationFilter | {}>({
    processorIds: [1, 2]
  });
  private refreshReconciliationTrigger = new Subject<void>();
  refreshReconciliationGrid$ = this.refreshReconciliationTrigger.asObservable();

  private readonly reconSearchUrl = 'dbov2/payment-reconciliation/recon-search';
  private readonly fmsInvoiceUrl = 'dbov2/fms-invoice';

  constructor(private http:HttpClient) {}

  getPaymentReceivedFilter(): Observable<AdxPaymentFilter | {}> {
    return this._dataSourceReceived$.asObservable();
  }

  setPaymentReceivedFilter(value:AdxPaymentFilter): void {
    this._dataSourceReceived$.next(value || null);
  }

  getPaymentMadeFilter(): Observable<AdxPaymentFilter | {}> {
    return this._dataSourceMade$.asObservable();
  }

  setPaymentMadeFilter(value:AdxPaymentFilter): void {
    this._dataSourceMade$.next(value || null);
  }

  getPaymentsReconciliationFilter(): Observable<PaymentReconciliationFilter | {}> {
    return this._dataSourceReconciliation$.asObservable();
  }

  setPaymentsReconciliationFilter(value:PaymentReconciliationFilter): void {
    const currentFilter = this._dataSourceReconciliation$.value as PaymentReconciliationFilter;
    if (value && Object.keys(currentFilter).length > 0) {
      const mergedFilter = { ...currentFilter, ...value };
      this._dataSourceReconciliation$.next(mergedFilter);
    } else {
      this._dataSourceReconciliation$.next(value || { processorIds: [1, 2] });
    }

    this.triggerReconciliationGridRefresh();
  }

  triggerReconciliationGridRefresh() {
    this.refreshReconciliationTrigger.next();
  }

  getAdxPaymentFilters(filters?: AdxPaymentFilter): Record<string, string | number | boolean> {
    return {
      paymentDateFrom: filters?.paymentDateFrom ? moment(filters?.paymentDateFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
      paymentDateTo: filters?.paymentDateTo ? moment(filters?.paymentDateTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
      profileTypeCode: filters?.profileTypeCode ?? '',
      payTypeId: filters?.payTypeId ?? '',
      payMethodId: filters?.payMethod ?? '',
      branchId: filters?.branch ?? '',
      bankId: filters?.bank ?? '',
      adxInvoiceId: filters?.invoiceId ?? '',
      clientName: filters?.payeeName ?? '',
      tripId: filters?.tripId ?? '',
      adxInvoiceRecordLocator: (filters?.['tripRecordLocator'] as string) ?? (filters?.['adxInvoiceRecordLocator'] as string) ?? '',
    }
  }

  searchAdxPayment({filters,page,sort}:SearchAdxPaymentParams): Observable<AdxPaymentResponse> {
    const params:Record<string, string | number | boolean> = {
      pageSize:  page?.pageSize || 25,
      pageNumber : (page?.pageIndex || 0) + 1,
      propertyName: this.getMappedPaymentsTblPropertyName(sort?.active|| ''),
      orderKey: sort?.direction?.toUpperCase() || '',
      ...this.getAdxPaymentFilters(filters),
    };

    removeEmptyParams(params);

    return this.http.get<AdxPaymentResponse>('dbov2/adx-payment-detail/search',
      { params: {...params}}
    ).pipe(catchError((error:HttpErrorResponse)=>{
      throw new Error(error?.message || 'Server error');
    }));
  }

  createAdxPayment(params: CreateAdxPaymentModel): Observable<any> {
    return this.http.post<any>('dbov2/adx-payment-detail',{ ...params }
    ).pipe(catchError((error:HttpErrorResponse)=>{
      throw new Error(error?.message || 'Server error');
    }));
  }

  triggerAdxPaymentGridRefresh() {
    this.refreshAdxPaymentTrigger.next();
  }

  getMappedPaymentsTblPropertyName(propertyName: string): string {
    switch (propertyName) {
      case 'fmsAdxPaymentDetailId':
        return 'Id';
      case 'vendorName':
        return 'adxBooking.Vendor.VendorName';
      case 'payTypeName':
        return 'payType.PayTypeName';
      case 'payMethodName':
        return 'payMethod.PaymethodName';
      case 'profileTypeName':
        return 'profileType.ProfileTypeName';
      case 'branchName':
        return 'branch.Name';
      case 'bankName':
        return 'bank.Name';
      case 'tripExternalReference':
        return 'trip.ExternalReference';
      case 'adxInvoiceRecordLocator':
        return 'trip.ExternalReference';
      case 'tripRecordLocator':
        return 'trip.ExternalReference';
      default:
        return propertyName;
    }
  }

  searchAdxPaymentReconciliation({filters,page,sort}:SearchAdxPaymentReconciliationParams): Observable<AdxPaymentReconciliationResponse> {
    const params:Record<string, string | number | boolean> = {
      pageSize:  page?.pageSize || 25,
      pageNumber : (page?.pageIndex || 0) + 1,
      propertyName: this.getMappedPaymentsTblPropertyName(sort?.active|| ''),
      orderKey: sort?.direction?.toUpperCase() || '',
      ...filters,
    };

    removeEmptyParams(params);

    return this.http.get<AdxPaymentReconciliationResponse>('dbov2/adx-payment-detail/recon',
      { params: {...params} }
    ).pipe(catchError((error:HttpErrorResponse)=>{
      throw new Error(error?.message || 'Server error');
    }));
  }

  searchAdxReconciliation({ filters, page, sort }: SearchAdxPaymentReconciliationParams): Observable<any> {
    const params: Record<string, string | number | boolean> = {
      pageSize: page?.pageSize || 25,
      pageNumber: (page?.pageIndex || 0) + 1,
      orderKey: sort?.direction?.toUpperCase() || '',
      ...filters,
    };

    removeEmptyParams(params);

    return this.http.get<any>('dbov2/adx-payment-detail/reconciliation',
      { params: { ...params } }
    ).pipe(catchError((error: HttpErrorResponse) => {
      throw new Error(error?.message || 'Server error');
    }));
  }

  adjustAdxPaymentReconciliation(elementAdjust: AdxPaymentReconciliationAdjustReq): Observable<any>{
    return this.http.post('dbov2/adx-payment-detail/adjust',
      { ...elementAdjust }
    ).pipe(catchError((error:HttpErrorResponse)=>{
      throw new Error(error?.message || 'Server error');
    }));
  }

  adjustmentAdxPaymentReconciliation(elementAdjust: AdxPaymentReconciliationAdjustmentOrgReq): Observable<any> {
    return this.http.post('dbov2/adx-payment-detail/reconciliation-adjustment',
      { ...elementAdjust }
    ).pipe(catchError((error: HttpErrorResponse) => {
      throw new Error(error?.message || 'Server error');
    }));
  }

  getReconciliation({ filters, page, sort }: SearchReconciliationParams): Observable<any> {
    const storedFilter = this._dataSourceReconciliation$.value as PaymentReconciliationFilter;
    let effectiveFilters: PaymentReconciliationFilter = { processorIds: [1, 2] };

    if (storedFilter && Object.keys(storedFilter).length > 0) {
      Object.assign(effectiveFilters, storedFilter);
    }

    if (filters && Object.keys(filters).length > 0) {
      const gridFilters = filters;
      Object.keys(gridFilters).forEach(key => {
        const filterValue = gridFilters[key as keyof typeof gridFilters];
        if (filterValue !== null && filterValue !== undefined) {
          if ((key !== 'adxReference' && key !== 'lastFourCC') || !effectiveFilters[key]) {
            effectiveFilters[key] = filterValue;
          }
        }
      });
    }

    filters = effectiveFilters;

    const params: Record<string, string | number | boolean | any> = {
      pageSize: page?.pageSize || 25,
      pageNumber: (page?.pageIndex || 0) + 1,
    };

    if (filters) {
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value !== null && value !== undefined) {
          if (key === 'processorIds' && Array.isArray(value)) {
            params[key] = value;
          }
          else if (key === 'adxReference') {
            params[key] = String(value);
          }
          else if (key === 'lastFourCC') {
            params[key] = String(value);
          }
          else {
            params[key] = value;
          }
        }
      });

      if (filters.processorIds && !params['processorIds']) {
        params['processorIds'] = filters.processorIds;
      }

      if (filters.reconType && !params['reconType']) {
        params['reconType'] = filters.reconType;
      }

      if (filters.adxReference && !params['adxReference']) {
        if (typeof filters.adxReference === 'object' && filters.adxReference !== null) {
          const adxRef = filters.adxReference as any;
          if (adxRef.value) {
            params['adxReference'] = String(adxRef.value);
          } else if (adxRef.toString) {
            params['adxReference'] = adxRef.toString();
          }
        } else if (filters.adxReference !== null) {
          params['adxReference'] = String(filters.adxReference);
        }
      }

      if (filters.lastFourCC && !params['lastFourCC']) {
        if (typeof filters.lastFourCC === 'object' && filters.lastFourCC !== null) {
          const lastFour = filters.lastFourCC as any;
          if (lastFour.value) {
            params['lastFourCC'] = String(lastFour.value);
          } else if (lastFour.toString) {
            params['lastFourCC'] = lastFour.toString();
          }
        } else if (filters.lastFourCC !== null) {
          params['lastFourCC'] = String(filters.lastFourCC);
        }
      }

      if (filters.isMatched !== undefined && filters.isMatched !== null) {
        params['isMatched'] = filters.isMatched;
      }

      if (filters.releaseDateFrom) {
        params['releaseDateFrom'] = filters.releaseDateFrom;
      }

      if (filters.releaseDateTo) {
        params['releaseDateTo'] = filters.releaseDateTo;
      }
    }

    if (sort?.active && sort?.direction) {
      params['propertyName'] = sort.active;
      params['orderKey'] = sort.direction.toUpperCase();
    }

    removeEmptyParams(params);

    const isMatchedValue = filters?.isMatched ?? undefined;
    if(isMatchedValue !== undefined) {
      params['isMatched'] = filters!.isMatched as boolean;
    }
    if (params['releaseDateFrom'] && typeof params['releaseDateFrom'] === 'string') {
      params['releaseDateFrom'] = moment(params['releaseDateFrom'] as string).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    }
    if (params['releaseDateTo'] && typeof params['releaseDateTo'] === 'string') {
      params['releaseDateTo'] = moment(params['releaseDateTo'] as string).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    }
    let url = `${this.reconSearchUrl}`;

    if(params['releaseDateFrom'] || params['releaseDateTo']) {
      url = `${this.reconSearchUrl}-extended`;
    }

    const httpParams: Record<string, string | number | boolean | string[] | number[]> = {};

    Object.keys(params).forEach(key => {
      const value = params[key];

      if (Array.isArray(value)) {
        httpParams[key] = value;
      } else {
        httpParams[key] = value;
      }
    });

    return this.http.get<any>(url,
      { params: httpParams }
    ).pipe(catchError(() => {
      return of({
        items: [],
        pagination: {
          hasPreviousPage: false,
          pageNumber: 1,
          hasNextPage: false,
          pageSize: params['pageSize'] || 25,
          totalPages: 0,
          totalItemCount: 0,
        }
      });
    }));
  }

  getInvoiceDetails(invoiceId: number): Observable<InvoiceDetails> {
    return this.http.get<InvoiceDetails>(`${this.fmsInvoiceUrl}/${invoiceId}`)
      .pipe(catchError((error: HttpErrorResponse) => {
        throw new Error(error?.message || 'Server error');
      }));
  }
}
